import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, ensureAccessToken } from "./api";
import { authStore } from "./authStore";
import { analytics } from "./analytics";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);

  const load = useCallback(async () => {
    // A cold start has no access token — it lives in sessionStorage and died
    // with the last tab — but the refresh token persists, so the session is
    // still valid. Returning null here was what actually signed customers out
    // on every browser restart; the 15-minute access TTL never did.
    const t = await ensureAccessToken();
    if (!t) { setUser(null); setReady(true); return; }
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
    } catch (err) {
      console.error("[atelier] /auth/me failed, clearing session", err);
      authStore.clear();
      setUser(null);
    }
    setReady(true);
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const expire = () => {
      setUser(null);
      setReady(true);
    };
    window.addEventListener("atelier:auth-expired", expire);
    return () => window.removeEventListener("atelier:auth-expired", expire);
  }, []);

  // Accepts either identifier: OTP sign-up keys a customer to a phone and never
  // requires an address, so an email-only login is unusable for most accounts.
  const login = useCallback(async (identifier, password) => {
    const payload = String(identifier || "").includes("@")
      ? { email: identifier, password }
      : { phone: identifier, password };
    const r = await api.post("/auth/login", payload);
    authStore.setTokens(r.data.tokens.access_token, r.data.tokens.refresh_token);
    setUser(r.data.user);
    return r.data.user;
  }, []);

  const register = useCallback(async (payload) => {
    const r = await api.post("/auth/register", payload);
    authStore.setTokens(r.data.tokens.access_token, r.data.tokens.refresh_token);
    setUser(r.data.user);
    analytics.signUp(payload?.email ? "email" : "phone");
    return r.data.user;
  }, []);

  const requestOtp = useCallback(async (phone, purpose = "login") => {
    const r = await api.post("/auth/otp/request", { phone, purpose });
    return r.data;
  }, []);

  const verifyOtp = useCallback(async (payload) => {
    const r = await api.post("/auth/otp/verify", payload);
    authStore.setTokens(r.data.tokens.access_token, r.data.tokens.refresh_token);
    setUser(r.data.user);
    return r.data;
  }, []);

  const logout = useCallback(async () => {
    const rt = authStore.getRefreshToken();
    if (rt) {
      try { await api.post("/auth/logout", { refresh_token: rt }); }
      catch (err) { console.error("[atelier] logout failed", err); }
    }
    authStore.clear();
    setUser(null);
  }, []);

  return (
    <AuthCtx.Provider value={{ user, ready, login, register, requestOtp, verifyOtp, logout, reload: load }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
